import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, Empty } from "@/components/shared";
import { fmtDateTime } from "@/lib/format";
import { LifeBuoy, Plus, X, Inbox, Loader, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PRIO = { High: "text-red-600 bg-red-50", Medium: "text-amber-600 bg-amber-50", Low: "text-slate-500 bg-slate-100" };

export default function Support() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/support/tickets", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data));
    api.get("/support/stats").then((r) => setStats(r.data));
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (t, status) => {
    await api.patch(`/support/tickets/${t.id}`, { status, resolution: status === "resolved" ? "Resolved by support" : null });
    toast.success(`Ticket ${status}`); load();
  };

  return (
    <div>
      <PageHeader title="Support & Helpdesk" subtitle="Technical support and helpdesk ticketing (Priority-2, live)." testid="support-page"
        actions={<button data-testid="new-ticket-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> New Ticket</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Total Tickets" value={stats?.total ?? "—"} accent="#2563EB" icon={LifeBuoy} testid="tk-total" />
        <StatCard label="Open" value={stats?.open ?? "—"} accent="#DC2626" icon={Inbox} testid="tk-open" />
        <StatCard label="In Progress" value={stats?.in_progress ?? "—"} accent="#D97706" icon={Loader} testid="tk-progress" />
        <StatCard label="Resolved" value={stats?.resolved ?? "—"} accent="#059669" icon={CheckCircle2} testid="tk-resolved" />
      </div>

      <div className="flex gap-2 mb-4">
        {["", "open", "in_progress", "resolved"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${filter === s ? "text-white" : "bg-white border border-slate-200 text-slate-600"}`} style={filter === s ? { background: "var(--navy-800)" } : {}}>{s ? s.replace("_", " ") : "All"}</button>
        ))}
      </div>

      <SectionCard className="!p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-5 py-3">Ticket</th><th className="px-5 py-3">Subject</th><th className="px-5 py-3">Category</th><th className="px-5 py-3">Priority</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Raised</th><th className="px-5 py-3">Action</th>
            </tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b border-slate-50">
                  <td className="px-5 py-3 font-mono text-xs">{t.ticket_no}</td>
                  <td className="px-5 py-3 text-slate-800">{t.subject}</td>
                  <td className="px-5 py-3 text-slate-600">{t.category}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRIO[t.priority]}`}>{t.priority}</span></td>
                  <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-5 py-3 text-xs text-slate-400">{fmtDateTime(t.created_at)}</td>
                  <td className="px-5 py-3">
                    <select defaultValue={t.status} onChange={(e) => setStatus(t, e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1 bg-white" data-testid={`ticket-status-${t.ticket_no}`}>
                      {["open", "in_progress", "resolved"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <Empty />}
        </div>
      </SectionCard>

      {open && <TicketDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); toast.success("Ticket raised"); }} />}
    </div>
  );
}

function TicketDialog({ onClose, onDone }) {
  const [f, setF] = useState({ subject: "", category: "Technical", priority: "Medium", description: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.subject) { toast.error("Subject required"); return; }
    try { await api.post("/support/tickets", f); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="ticket-dialog">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">New Support Ticket</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Subject" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ticket-subject" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{["Technical", "Rewards", "Access", "Finance", "Other"].map((c) => <option key={c}>{c}</option>)}</select>
            <select value={f.priority} onChange={(e) => set("priority", e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{["High", "Medium", "Low"].map((p) => <option key={p}>{p}</option>)}</select>
          </div>
          <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the issue…" rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <button onClick={submit} data-testid="ticket-submit" className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Raise Ticket</button>
        </div>
      </div>
    </div>
  );
}
